import { useState, useEffect } from "react";
import { Box, Button, Checkbox, Modal, TextField, Typography, CircularProgress } from "@mui/material";
import Layout from "../../../Components/Layout";
import { FRINGE_BENEFIT_METHODS } from "../../../../utils/services/apiPath";
import { getApi, putApi, postApi } from "../../../../utils/services";
import { errorToast, successToast } from "../../../../utils/toastHelper";
import ProGrid from "../../../Components/ProGrid/v2";
import { useNavigate } from "react-router-dom";

export default function FringeBenefitGrid() {
  const navigation = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRow, setSelectedRow] = useState(null);
  const [open, setOpen] = useState(false);
  const [emptyGuid] = useState("00000000-0000-0000-0000-000000000000");

  const fetchData = async () => {
    try {
      const { data } = await getApi(`${FRINGE_BENEFIT_METHODS}`);
      const dataWithId = data.map((row, index) => ({
        ...row,
        id: index + 1
      }));
      setRows(dataWithId);
    } catch (err) {
      errorToast("Failed to fetch Fringe Benefit Accounts.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleEditClick = row => {
    setSelectedRow({ ...row });
    setOpen(true);
  };

  const handleChange = (field, value) => {
    setSelectedRow(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveModal = async () => {
    if (selectedRow.isFunded && !selectedRow.accountNumber) {
      alert("Account Number is required if Funded is selected");
      return;
    }
    const payload = {
      fringeBenefitPlanName: selectedRow.fringeBenefitPlanName || "",
      isFunded: selectedRow.isFunded || false,
      accountNumber: selectedRow.accountNumber || "",
      isActive: selectedRow.isActive || false
    };

    try {
      
      if (selectedRow.fringeBenefitAccountGuid === emptyGuid) {
        const { error } = await postApi(FRINGE_BENEFIT_METHODS, { ...payload, fringeBenefitType: selectedRow.fringeBenefitType });
        if (error) return errorToast(error);
      } else {
        const { error } = await putApi(`${FRINGE_BENEFIT_METHODS}/${selectedRow.fringeBenefitAccountGuid}`, payload);
        if (error) return errorToast(error);
      }
      fetchData();
      successToast("Fringe Benefit Settings saved successfully.");
      setOpen(false);
    } catch (err) {
      errorToast("Failed to save settings.");
    }
  };

  const columns = [
    {
      field: "fringeBenefitTypeName",
      headerName: "Fringe Benefit Type",
      flex: 1.5,
      renderCell: params => (
        <Button onClick={() => handleEditClick(params.row)} variant="text">
          {params.value}
        </Button>
      )
    },
    { field: "fringeBenefitPlanName", headerName: "Plan Name", flex: 1.5 },
    {
      field: "isFunded",
      headerName: "Funded",
      flex: 0.6,
      renderCell: params => <Checkbox checked={params.value} disabled />
    },
    {
      field: "accountNumber",
      headerName: "Account Number",
      flex: 1.2
    },
    {
      field: "isActive",
      headerName: "Active",
      flex: 0.6,
      renderCell: params => <Checkbox checked={params.value} disabled />
    }
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  const addButtonAction = () => {
    navigation("/settings/fringe-benefit-accounts/add");
  };

  return (
    <Layout canView={true}>
      <ProGrid
        title="Fringe Benefit Accounts"
        rows={rows}
        columns={columns}
        disableRowSelectionOnClick
        hideFooter={false}
        hideSearch={true}
        hideExport={true}
        addButtonLabel="+ Add Fringe Benefit Accounts"
        addButtonAction={addButtonAction}
        options={{
          pageSizeOptions: []
          // paginationModel: { pageSize: 10, page: 0 }
        }}
      />

      <Modal open={open} onClose={() => setOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "white",
            boxShadow: 24,
            p: 4,
            width: 400,
            borderRadius: 2
          }}
        >
          <Typography variant="h6" mb={2}>
            Edit {selectedRow?.fringeBenefitTypeName}
          </Typography>

          <TextField
            label="Fringe Benefit Plan Name"
            fullWidth
            value={selectedRow?.fringeBenefitPlanName || ""}
            onChange={e => handleChange("fringeBenefitPlanName", e.target.value)}
            margin="normal"
          />

          <Box display="flex" alignItems="center" mb={1}>
            <Checkbox checked={selectedRow?.isFunded || false} onChange={e => handleChange("isFunded", e.target.checked)} />
            <Typography>Funded</Typography>
          </Box>

          <TextField
            label="Account Number"
            fullWidth
            value={selectedRow?.accountNumber || ""}
            onChange={e => handleChange("accountNumber", e.target.value)}
            margin="normal"
            required={selectedRow?.isFunded}
            error={selectedRow?.isFunded && !selectedRow?.accountNumber}
          />

          <Box display="flex" alignItems="center" mb={2}>
            <Checkbox checked={selectedRow?.isActive || false} onChange={e => handleChange("isActive", e.target.checked)} />
            <Typography>Active</Typography>
          </Box>

          <Button variant="contained" fullWidth onClick={handleSaveModal}>
            Save
          </Button>
        </Box>
      </Modal>
    </Layout>
  );
}
